import React from 'react';
import PropTypes from 'prop-types';
import useCards from '../Cards/useCards';
import { useDispatch, useSelector } from 'react-redux';
import { attach, evolve } from '../Pokemon/actions';
import { selectAttachFlowing, selectAttaching, selectEvolveFlowing, selectEvolving } from '../Pokemon/selectors';
import { selectEvolveTargets } from '../Board/selectors';
import { selectActivePokemon, selectIsActivePlayer } from '../ActiveGame/selectors';
import { PlainButton } from '../Controls';
import CardFace from '../Card/CardFace';
import { gemSeed } from '../Card/gemLayout';
import { Slot } from '../Card/styled';
import { ACTIVE } from '../ActiveGame/contextMenuTypes';
import { setContextMenu } from '../ActiveGame/actions';
import { conversionBadges } from '../Card/conversions';
import usePickTarget from '../Picks/usePickTarget';
import DropTarget from '../Drag/DropTarget';
import { useDragging } from '../Drag/dropTargets';
import { MAKE_ACTIVE } from '../Hand/handCardAction';
import { anchors } from '../Motion/anchorNames';
import { putActivePokemon } from '../GameStart/actions';
import { selectCurrentUser } from '../Login/selectors';
import usePokemonDrags from '../Board/usePokemonDrags';
import DraggablePokemon from '../Board/DraggablePokemon';
import { ACTIVE_TO_BENCH, BENCHED_TO_ACTIVE } from '../Board/pokemonDrags';

const ActivePokemon = ({ userId }) => {
  const dispatch = useDispatch();
  const attachFlowing = useSelector(selectAttachFlowing);
  const attaching = useSelector(selectAttaching);
  const evolveFlowing = useSelector(selectEvolveFlowing);
  const evolveTargets = useSelector(selectEvolveTargets);
  const evolving = useSelector(selectEvolving);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const attachedTrainers = useCards(activePokemon && activePokemon.get('trainer_card_ids'));
  const isActivePlayer = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const isMine = useSelector(selectCurrentUser).get('id') === userId;
  const dragging = useDragging();
  const pickTarget = usePickTarget(
    userId,
    'active',
    activePokemon ? activePokemon.get('energyAttachments') : undefined,
  );
  const drags = usePokemonDrags(userId);
  // A benched Pokémon of yours dragged up here: what dropping it does (usePokemonDrags).
  const upHere = isMine && dragging && dragging.kind === BENCHED_TO_ACTIVE
    ? () => drags.goUp(dragging.how, dragging.benchPosition)
    : null;

  if (!activePokemon) {
    // While setting up, a Basic Pokémon dragged from the hand can be put here (tapping it in the hand does the same).
    if (isMine && dragging && dragging.kind === MAKE_ACTIVE) {
      return (
        <>
          <DropTarget name={anchors.active(userId)} commit={() => dispatch(putActivePokemon(dragging.handPosition))} />
          <Slot $targetable aria-label="No active Pokémon">+</Slot>
        </>
      );
    }
    if (upHere) {
      return (
        <>
          <DropTarget name={anchors.active(userId)} commit={upHere} />
          <Slot $targetable aria-label="No active Pokémon">+</Slot>
        </>
      );
    }
    return <Slot aria-label="No active Pokémon" />;
  }

  let selectActiveAction = () => dispatch(setContextMenu(ACTIVE, userId));
  // Whether a flow is picking this Pokémon: then a tap (or a drop) is the pick, with no question first; the turn
  // banner's Cancel backs out of the flow.
  let picking = false;
  let busy = false;

  if (isActivePlayer && attachFlowing) {
    selectActiveAction = () => dispatch(attach());
    picking = true;
    busy = attaching;
  }

  // Only a Pokémon the server says the card can evolve lights up.
  if (isActivePlayer && evolveFlowing && evolveTargets.includes('active')) {
    selectActiveAction = () => dispatch(evolve());
    picking = true;
    busy = evolving;
  }

  // A Trainer or Power can pick either player's Pokémon; the hook knows which.
  if (pickTarget) {
    selectActiveAction = pickTarget.onClick;
    picking = true;
    busy = pickTarget.busy;
  }

  return (
    <>
      {picking && !busy && <DropTarget name={anchors.active(userId)} commit={selectActiveAction} />}
      {upHere && <DropTarget name={anchors.active(userId)} commit={upHere} />}
      <PlainButton onClick={selectActiveAction} disabled={busy} aria-label={activePokemon.getIn(['card', 'name'], 'Active Pokémon')}>
        <DraggablePokemon
          enabled={drags.activeDrags}
          place={anchors.active(userId)}
          lift={() => ({ kind: ACTIVE_TO_BENCH })}
          settledBy={activePokemon}
        >
          <CardFace
            card={activePokemon.get('card')}
            damage={activePokemon.get('damage')}
            damageSeed={gemSeed.active(activePokemon.get('id'))}
            conditions={activePokemon.get('conditions')}
            protection={activePokemon.get('protection')}
            hindrance={activePokemon.get('hindrance')}
            hinderedBy={activePokemon.get('hindered_by')}
            trainers={attachedTrainers}
            conversions={conversionBadges(activePokemon)}
            energyAttachments={activePokemon.get('energyAttachments')}
            targetable={(picking && !busy) || !!upHere}
          />
        </DraggablePokemon>
      </PlainButton>
    </>
  );
};

ActivePokemon.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default ActivePokemon;
